import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useVeloraStore } from '../store/useVeloraStore';
import type { WarmupStep } from '@shared/protocol';

const DEFAULT_WARMUPS: Record<string, WarmupStep[]> = {
  '5min': [
    { name: 'Deep Breathing', duration_seconds: 60, description: 'Breathe in for 4 counts, out for 8. Relax your jaw.', type: 'breathing' },
    { name: 'Lip Trills', duration_seconds: 60, description: 'Trill your lips on a descending 5-note scale. Relax shoulders.', type: 'scales' },
    { name: 'Humming Scale', duration_seconds: 60, description: 'Hum up and down a full octave scale, forward placement.', type: 'scales' },
    { name: 'Arpeggios', duration_seconds: 90, description: 'Sing "1-3-5-3-1" arpeggio pattern, moving up by semitones.', type: 'arpeggios' },
    { name: 'Free Sing', duration_seconds: 30, description: 'Lightly sing a phrase from any song you know.', type: 'song' },
  ],
  '10min': [
    { name: 'Deep Breathing', duration_seconds: 90, description: 'Box breathing: 4 in, 4 hold, 4 out, 4 hold.', type: 'breathing' },
    { name: 'Sirens', duration_seconds: 60, description: 'Glide from your lowest to highest note like a siren. Both directions.', type: 'scales' },
    { name: 'Lip Trills', duration_seconds: 90, description: 'Lip trills on a full octave scale, ascending and descending.', type: 'scales' },
    { name: 'Vowel Scales', duration_seconds: 90, description: 'Sing "Ah-Eh-Ee-Oh-Oo" on a 5-note scale, focus on resonance.', type: 'scales' },
    { name: 'Arpeggios', duration_seconds: 120, description: 'Major and minor arpeggio pattern across two octaves.', type: 'arpeggios' },
    { name: 'Staccato', duration_seconds: 90, description: 'Rapid staccato scales on "Ha-Ha-Ha" to engage diaphragm.', type: 'scales' },
    { name: 'Song Phrase', duration_seconds: 60, description: 'Sing a challenging phrase from a target song.', type: 'song' },
  ],
};

export default function Warmup() {
  const theme = useTheme();
  const { warmupSteps, isConnected } = useVeloraStore();
  const [selectedDuration, setSelectedDuration] = useState<'5min' | '10min' | 'custom'>('5min');
  const [steps, setSteps] = useState<WarmupStep[]>(DEFAULT_WARMUPS['5min']);
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTimeLeft, setStepTimeLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (warmupSteps && warmupSteps.length > 0 && selectedDuration === 'custom') setSteps(warmupSteps);
  }, [warmupSteps, selectedDuration]);

  const totalSeconds = steps.reduce((a, s) => a + s.duration_seconds, 0);

  const start = useCallback(() => {
    setActive(true); setCurrentStep(0);
    setStepTimeLeft(steps[0]?.duration_seconds ?? 0);
  }, [steps]);

  const stop = useCallback(() => {
    setActive(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  useEffect(() => {
    if (!active) return;
    intervalRef.current = setInterval(() => {
      setStepTimeLeft(prev => {
        if (prev <= 1) {
          setCurrentStep(cs => {
            const next = cs + 1;
            if (next >= steps.length) { stop(); return cs; }
            setStepTimeLeft(steps[next].duration_seconds);
            return next;
          });
          return steps[currentStep + 1]?.duration_seconds ?? 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [active, currentStep, steps, stop]);

  const stepColor = (type: WarmupStep['type']) => {
    const map = { breathing: '#60a5fa', scales: '#7F77DD', arpeggios: '#a78bfa', song: '#4ade80', custom: '#AFA9EC' };
    return map[type];
  };

  const step = steps[currentStep];
  const doneSeconds = steps.slice(0, currentStep).reduce((a, s) => a + s.duration_seconds, 0);
  const progressPct = ((doneSeconds + (step ? step.duration_seconds - stepTimeLeft : 0)) / totalSeconds) * 100;

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-5 fade-up">
        <p className="text-xs tracking-widest uppercase font-medium mb-1"
          style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Practice Routine</p>
        <h1 className="text-2xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>WARMUP</h1>
      </div>

      {!active ? (
        <div className="px-4 flex flex-col gap-3">
          {/* Duration selector */}
          <div className="fade-up-1 flex gap-2">
            {(['5min', '10min', 'custom'] as const).map(d => (
              <button key={d} onClick={() => {
                setSelectedDuration(d);
                if (d !== 'custom') setSteps(DEFAULT_WARMUPS[d]);
                else if (warmupSteps?.length) setSteps(warmupSteps);
              }}
                className="flex-1 py-2.5 rounded-2xl text-sm font-semibold transition-all active:scale-95 glass-card"
                style={{
                  background: selectedDuration === d ? 'rgba(83,74,183,0.25)' : undefined,
                  borderColor: selectedDuration === d ? 'rgba(127,119,221,0.45)' : undefined,
                  color: selectedDuration === d ? theme.text : theme.muted,
                  fontFamily: 'Syne, sans-serif',
                  boxShadow: selectedDuration === d ? `0 4px 16px rgba(83,74,183,0.2)` : undefined,
                }}>
                {d === 'custom' ? `AI${isConnected ? ' ✓' : ''}` : d}
              </button>
            ))}
          </div>

          {/* Step list */}
          <div className="fade-up-2 space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="glass-card rounded-xl flex items-center gap-3 p-3">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: stepColor(s.type), boxShadow: `0 0 6px ${stepColor(s.type)}80` }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: theme.text }}>{s.name}</p>
                  <p className="text-xs truncate" style={{ color: theme.muted }}>{s.description}</p>
                </div>
                <span className="text-xs font-mono shrink-0" style={{ color: theme.dim }}>{s.duration_seconds}s</span>
              </div>
            ))}
          </div>

          <div className="fade-up-3 text-center text-sm" style={{ color: theme.muted }}>
            Total: {Math.floor(totalSeconds / 60)}:{String(totalSeconds % 60).padStart(2, '0')}
          </div>

          <button onClick={start}
            className="fade-up-4 w-full py-4 rounded-2xl text-base font-semibold transition-all active:scale-95"
            style={{
              background: 'rgba(83,74,183,0.28)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(127,119,221,0.40)',
              color: theme.text,
              fontFamily: 'Syne, sans-serif',
              boxShadow: '0 8px 24px rgba(83,74,183,0.25), inset 0 1px 0 rgba(255,255,255,0.12)',
            }}>
            Start Warmup
          </button>
        </div>
      ) : (
        <div className="px-4 flex flex-col gap-4">
          {/* Progress bar */}
          <div className="fade-up h-1.5 glass-card rounded-full overflow-hidden" style={{ borderRadius: 50 }}>
            <div className="h-full rounded-full transition-all duration-1000"
              style={{
                width: `${progressPct}%`,
                background: `linear-gradient(90deg, ${theme.primary}, ${theme.light})`,
                boxShadow: `0 0 8px ${theme.accent}60`,
              }} />
          </div>
          <p className="text-xs text-center" style={{ color: theme.muted }}>
            Step {currentStep + 1} of {steps.length}
          </p>

          {/* Current step */}
          {step && (
            <div className="fade-up-1 glass-card rounded-3xl p-6 text-center"
              style={{ background: `${stepColor(step.type)}10`, borderColor: `${stepColor(step.type)}35` }}>
              <div className="text-xs tracking-widest uppercase mb-1"
                style={{ color: stepColor(step.type), fontFamily: 'Syne, sans-serif' }}>
                {step.type}
              </div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
                {step.name}
              </h2>
              <svg width="100" height="100" viewBox="0 0 36 36" className="mx-auto mb-4">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.8"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={stepColor(step.type)} strokeWidth="2.8"
                  strokeDasharray={`${(stepTimeLeft / step.duration_seconds) * 100} 100`}
                  strokeDashoffset="25" strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 1s linear', filter: `drop-shadow(0 0 4px ${stepColor(step.type)}60)` }}/>
                <text x="18" y="22" textAnchor="middle" fontSize="10" fill={theme.text} fontFamily="JetBrains Mono, monospace">
                  {stepTimeLeft}s
                </text>
              </svg>
              <p className="text-sm leading-relaxed" style={{ color: theme.muted }}>{step.description}</p>
            </div>
          )}

          {steps[currentStep + 1] && (
            <div className="fade-up-2 glass-card rounded-xl p-3.5">
              <p className="text-xs mb-1" style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Up next</p>
              <p className="text-sm font-medium" style={{ color: theme.text }}>{steps[currentStep + 1].name}</p>
            </div>
          )}

          <button onClick={stop} className="fade-up-3 w-full py-3 rounded-xl text-sm glass-card"
            style={{ borderColor: 'rgba(248,113,113,0.28)', color: '#f87171',
              background: 'rgba(248,113,113,0.08)' }}>
            Stop Warmup
          </button>
        </div>
      )}
    </div>
  );
}
